/**
 * Collapsible panel listing the currently-uploaded Wholesale Workspace
 * files with a red-X delete affordance. Direct mirror of the Daily Photo
 * List ActiveVendorFilesPanel — same chrome, same delete style, same
 * zebra-striped table. Adapted for the wholesale shape (Adesa + Tekion).
 *
 * Default collapsed; click the header to expand.
 */
import React, { useState } from "react"
import { ChevronDown, ChevronRight, X } from "lucide-react"

import { WholesaleFileSummary, WholesaleSession } from "../../api/wholesale"
import { THEME } from "../../config/theme"
import { useWholesale } from "../../context/WholesaleContext"
import { formatAge } from "../../utils/formatAge"

interface Props {
  session: WholesaleSession | null
}

interface FlatFile {
  fileId: number
  filename: string
  vendor: string
  rows: number
  ageSeconds: number | null
}

export default function ActiveVendorFilesPanel({ session }: Props) {
  const { deleteFile } = useWholesale()

  const [expanded, setExpanded] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const flatFiles = flatten(session)

  const handleDelete = async (file: FlatFile) => {
    if (!window.confirm(`Delete "${file.filename}"?`)) return
    setDeletingFileId(file.fileId)
    setDeleteError(null)
    try {
      await deleteFile(file.fileId)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Delete failed")
    } finally {
      setDeletingFileId(null)
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.md,
        backgroundColor: THEME.colors.neutral.light.surface,
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
          cursor: "pointer",
          userSelect: "none",
          fontSize: 13,
          fontWeight: 600,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        <span
          style={{
            width: 14,
            display: "inline-flex",
            alignItems: "center",
            color: THEME.colors.neutral.light.textSecondary,
          }}
        >
          {expanded ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
        </span>
        Active Vendor Files
        <span
          style={{
            fontWeight: 400,
            color: THEME.colors.neutral.light.textSecondary,
            marginLeft: 4,
          }}
        >
          ({flatFiles.length})
        </span>
      </div>

      {expanded && (
        <div style={{ padding: `0 ${THEME.spacing.md} ${THEME.spacing.md}` }}>
          {deleteError && (
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: "0.5px solid rgba(220, 38, 38, 0.3)",
                padding: "8px 12px",
                borderRadius: THEME.borderRadius.md,
                marginBottom: 8,
                fontSize: 12,
              }}
            >
              {deleteError}
            </div>
          )}
          {flatFiles.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: THEME.colors.neutral.light.textSecondary,
              }}
            >
              No files uploaded
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr
                  style={{
                    borderBottom: `1px solid ${THEME.colors.silver}`,
                    textAlign: "left",
                  }}
                >
                  <th style={thStyle}>Filename</th>
                  <th style={thStyle}>Vendor</th>
                  <th style={thStyle}>Rows</th>
                  <th style={thStyle}>Age</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flatFiles.map((f, i) => {
                  const isDeleting = deletingFileId === f.fileId
                  return (
                    <tr
                      key={f.fileId}
                      style={{
                        backgroundColor:
                          i % 2 === 0
                            ? THEME.auditTable.zebraLight
                            : THEME.auditTable.zebraMain,
                        opacity: isDeleting ? 0.5 : 1,
                      }}
                    >
                      <td style={tdStyle} title={f.filename}>
                        {f.filename.length > 60
                          ? f.filename.slice(0, 60) + "…"
                          : f.filename}
                      </td>
                      <td style={tdStyle}>{vendorLabel(f.vendor)}</td>
                      <td style={tdStyle}>{f.rows.toLocaleString()}</td>
                      <td style={tdStyle}>
                        {f.ageSeconds != null ? formatAge(f.ageSeconds) : "—"}
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => handleDelete(f)}
                          disabled={isDeleting}
                          title="Delete"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            background: "none",
                            border: "none",
                            color: THEME.colors.semantic.danger,
                            cursor: isDeleting ? "not-allowed" : "pointer",
                            padding: "2px 6px",
                          }}
                        >
                          <X size={16} strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

const thStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontWeight: 500,
  fontSize: 12,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const tdStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.neutral.light.textPrimary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 300,
}

function vendorLabel(vendor: string): string {
  if (vendor === "adesa") return "Adesa"
  if (vendor === "tekion") return "Tekion"
  return vendor
}

function flatten(session: WholesaleSession | null): FlatFile[] {
  if (!session) return []
  const out: FlatFile[] = []
  if (session.adesa) out.push(toFlat(session.adesa))
  if (session.tekion) out.push(toFlat(session.tekion))
  return out
}

function toFlat(file: WholesaleFileSummary): FlatFile {
  return {
    fileId: file.file_id,
    filename: file.filename,
    vendor: file.vendor,
    rows: file.row_count,
    ageSeconds: ageSecondsFromIso(file.uploaded_at),
  }
}

function ageSecondsFromIso(iso: string | null): number | null {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms) || ms < 0) return null
  return Math.floor(ms / 1000)
}
